import { useAppDispatch, useAppSelector } from 'app/hooks';
import ProjectSetting from 'components/common/project-setting';
import Multiselect from 'components/inputs/multiselect';
import Select from 'components/inputs/select';
import { updateProjectSetting } from 'features/project';
import { ReactNode, useEffect, useState } from 'react';
import { getProjectSettingValue } from 'utils/project';

const COMPONENT_OPTIONS = {
  heatingCooling: [
    'Natural Gas Furnace/AC/Evap Coil',
    'Liquid Propane Furnace/AC/Evap Coil',
    'Electric Furnace/AC/Evap Coil',
    'Oil Furnace/AC/Evap Coil',
    'Heat Pump/Air Handler',
    'Heat Pump/Evap Coil/Modular Blower',
    'Self Contained Packaged AC',
    'Self Contained Packaged Heat Pump',
    'Self Contained Packaged Gas/AC',
    'Self Contained Packaged Gas/Heat Pump',
    'Help Me Decide (Split System)',
    'Help Me Decide (Self Contained Unit)'
  ],
  heatingOnly: [
    'Natural Gas Furnace',
    'Liquid Propane Furnace',
    'Electric Furnace',
    'Oil Furnace',
    'Heat Pump/Air Handler',
    'Heat Pump/Evap Coil/Modular Blower',
    'Self Contained Packaged Gas/AC',
    'Self Contained Packaged Gas/Heat Pump',
    'Help Me Decide (Split System)',
    'Help Me Decide (Self Contained Unit)'
  ],
  coolingOnly: [
    'AC/Evap Coil',
    'AC/Air Handler',
    'Heat Pump/Air Handler',
    'Heat Pump/Evap Coil/Modular Blower',
    'Self Contained Packaged AC',
    'Self Contained Packaged Heat Pump',
    'Help Me Decide (Split System)',
    'Help Me Decide (Self Contained Unit)'
  ]
};

const SPLIT_SYSTEM_LOCATIONS = [
  'Attic',
  'Closet',
  'Basement',
  'Crawlspace',
  'Other'
];

const SELF_CONTAINED_LOCATIONS = ['Ground', 'Roof'];

const HELP_ME_DECIDE = [
  'Help Me Decide (Split System)',
  'Help Me Decide (Self Contained Unit)'
];

const HELP_ME_DECIDE_SPLIT = ['Help Me Decide (Split System)'];
const HELP_ME_DECIDE_SELF_CONTAINED = ['Help Me Decide (Self Contained Unit)'];

const FURNACE_TYPES = [
  'Natural Gas Furnace/AC/Evap Coil',
  'Liquid Propane Furnace/AC/Evap Coil',
  'Electric Furnace/AC/Evap Coil',
  'Oil Furnace/AC/Evap Coil'
];

const FURNACE_TYPES_SIMPLE = [
  'Natural Gas Furnace',
  'Liquid Propane Furnace',
  'Electric Furnace',
  'Oil Furnace'
];

const HEAT_PUMP_TYPES = [
  'Heat Pump/Air Handler',
  'Heat Pump/Evap Coil/Modular Blower'
];

const SELF_CONTAINED_TYPES = [
  'Self Contained Packaged AC',
  'Self Contained Packaged Heat Pump',
  'Self Contained Packaged Gas/AC',
  'Self Contained Packaged Gas/Heat Pump'
];

const COOLING_SPLIT_TYPES = ['AC/Evap Coil', 'AC/Air Handler'];

interface QuestionSectionProps {
  readonly title: string;
  readonly children: ReactNode;
}

function QuestionSection({ title, children }: QuestionSectionProps) {
  return (
    <div className='flex w-full flex-col items-center gap-5'>
      <span className='text-center text-2xl font-semibold'>{title}</span>
      <div>{children}</div>
    </div>
  );
}

function hasAnyComponent(selected: string[], options: string[]) {
  return selected.some((c) => options.includes(c));
}

interface ComponentConfig {
  fuelTypeTriggers?: string[];
  splitLocationTriggers?: string[];
  selfContainedTriggers?: string[];
}

export default function DuctedPreferencesForm() {
  const dispatch = useAppDispatch();
  const { existingSystems } = useAppSelector((state) => state.structure);
  const [systemConsideration, setSystemConsideration] = useState<string>('');
  const [componentConsideration, setComponentConsideration] = useState<
    string[]
  >([]);
  const [splitSystemLocation, setSplitSystemLocation] = useState<string>('');
  const [selfContainedLocation, setSelfContainedLocation] =
    useState<string>('');

  const checkFuelType = (
    fuelValue: string,
    comparison: 'equals' | 'notEquals'
  ) => {
    const compare =
      comparison === 'equals'
        ? (a: string | undefined, b: string) => a === b
        : (a: string | undefined, b: string) => a !== b;

    return existingSystems.some((system) =>
      system.components.some((component) => {
        if (component.type === 'Split System Furnace') {
          return compare(component.fuelType, fuelValue);
        }
        if (
          component.type === 'Self Contained Electric AC' ||
          component.type === 'Self Contained Electric Heat Pump'
        ) {
          return compare('Electricity', fuelValue);
        }
        if (
          component.type === 'Self Contained Gas Pack With AC' ||
          component.type === 'Self Contained Gas Pack With Heat Pump'
        ) {
          return compare('Natural Gas', fuelValue);
        }
        return false;
      })
    );
  };

  const existingEquipmentIsNotElectric = checkFuelType(
    'Electricity',
    'notEquals'
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const existingEquipmentUsesNaturalGas = checkFuelType(
    'Natural Gas',
    'equals'
  );

  useEffect(() => {
    setSystemConsideration('');
  }, [getProjectSettingValue('replacing_with_different_equipment')]);

  useEffect(() => {
    setComponentConsideration([]);
    setSplitSystemLocation('');
    setSelfContainedLocation('');
    dispatch(
      updateProjectSetting({
        attributeCode: 'consider_alternate_fuel_types',
        value: undefined
      })
    );
  }, [systemConsideration]);

  const showFuelTypeQuestion = (triggers: string[]) =>
    hasAnyComponent(componentConsideration, triggers) && (
      <QuestionSection title='Do you want to consider changing your energy source/fuel type?'>
        <ProjectSetting code='consider_alternate_fuel_types' />
      </QuestionSection>
    );

  const showSplitSystemLocation = (triggers: string[]) =>
    hasAnyComponent(componentConsideration, triggers) && (
      <QuestionSection title='What would be the best location for the split system indoor unit?'>
        <Select
          value={splitSystemLocation}
          onChange={(value) => setSplitSystemLocation(value as string)}
          options={SPLIT_SYSTEM_LOCATIONS}
          enableChips
        />
      </QuestionSection>
    );

  const showSelfContainedLocation = (triggers: string[]) =>
    hasAnyComponent(componentConsideration, triggers) && (
      <QuestionSection title='What would be the best location for the self contained unit?'>
        <Select
          value={selfContainedLocation}
          onChange={(value) => setSelfContainedLocation(value as string)}
          options={SELF_CONTAINED_LOCATIONS}
          enableChips
        />
      </QuestionSection>
    );

  const renderComponentSection = (
    options: string[],
    config: ComponentConfig
  ) => (
    <div className='flex w-full flex-col items-center gap-5'>
      <QuestionSection title='What types of components are you considering?'>
        <div className='w-[300px]'>
          <Multiselect
            value={componentConsideration}
            onChange={(value) => setComponentConsideration(value as string[])}
            options={options}
          />
        </div>
      </QuestionSection>
      {config.fuelTypeTriggers && showFuelTypeQuestion(config.fuelTypeTriggers)}
      {config.splitLocationTriggers &&
        showSplitSystemLocation(config.splitLocationTriggers)}
      {config.selfContainedTriggers &&
        showSelfContainedLocation(config.selfContainedTriggers)}
    </div>
  );

  return (
    <div className='flex w-full max-w-[700px] flex-col items-center gap-5'>
      <span className='mt-5 text-center text-2xl font-semibold'>
        Are you trying to replace your current equipment with a different type
        of equipment?
      </span>
      <div className='flex justify-center'>
        <ProjectSetting code='replacing_with_different_equipment' />
      </div>
      {getProjectSettingValue('replacing_with_different_equipment') === 'No' &&
        existingEquipmentIsNotElectric && (
          <QuestionSection title='Do you want to consider changing your energy source/fuel type?'>
            <ProjectSetting code='consider_alternate_fuel_types' />
          </QuestionSection>
        )}
      {getProjectSettingValue('replacing_with_different_equipment') ===
        'Yes' && (
        <div className='flex w-full flex-col items-center gap-5'>
          <QuestionSection title='What types of systems are you considering?'>
            <Select
              value={systemConsideration}
              onChange={(value) => setSystemConsideration(value as string)}
              options={['Heating & Cooling', 'Heating Only', 'Cooling Only']}
              enableChips
            />
          </QuestionSection>
          {systemConsideration === 'Heating & Cooling' &&
            renderComponentSection(COMPONENT_OPTIONS.heatingCooling, {
              fuelTypeTriggers: [...FURNACE_TYPES, ...HELP_ME_DECIDE],
              splitLocationTriggers: [
                ...FURNACE_TYPES,
                ...HEAT_PUMP_TYPES,
                ...HELP_ME_DECIDE_SPLIT
              ],
              selfContainedTriggers: [
                ...SELF_CONTAINED_TYPES,
                ...HELP_ME_DECIDE_SELF_CONTAINED
              ]
            })}
          {systemConsideration === 'Heating Only' &&
            renderComponentSection(COMPONENT_OPTIONS.heatingOnly, {
              fuelTypeTriggers: [...FURNACE_TYPES_SIMPLE, ...HELP_ME_DECIDE]
            })}
          {systemConsideration === 'Cooling Only' &&
            renderComponentSection(COMPONENT_OPTIONS.coolingOnly, {
              fuelTypeTriggers: HELP_ME_DECIDE,
              splitLocationTriggers: [
                ...COOLING_SPLIT_TYPES,
                ...HEAT_PUMP_TYPES,
                ...HELP_ME_DECIDE_SPLIT
              ],
              selfContainedTriggers: [
                'Self Contained Packaged AC',
                'Self Contained Packaged Heat Pump',
                ...HELP_ME_DECIDE_SELF_CONTAINED
              ]
            })}
        </div>
      )}
    </div>
  );
}
